import { useActivePractice } from '@witmetrics/data-store';
import type { DateRange } from '@witmetrics/utils';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { useReports } from '@/providers/ReportsProvider';
import { isTimeframeChanged } from '@/utils/time';
import DateRangeInput from '@/components/DateRangeInput';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { buildFileName, classes, fetchSummaryReport } from './utils';

export type SummaryReportDialogProps = {
  onClose: () => void;
};

export default function SummaryReportDialog({
  onClose,
}: SummaryReportDialogProps) {
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const { timeframe, onUpdateTimeframe } = useReports();
  const [isExporting, toggleExporting] = useToggle(false);

  const handleClose = () => {
    if (!isExporting) onClose();
  };

  const handleUpdate = (update: DateRange) => {
    if (isTimeframeChanged(timeframe, update)) {
      onUpdateTimeframe(update);
    }
  };

  const handleExport = async () => {
    try {
      toggleExporting(true);
      const reportExport = await fetchSummaryReport(
        activePractice!.id,
        timeframe
      );
      const url = window.URL.createObjectURL(reportExport);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildFileName();
      a.click();
      toggleExporting(false);
    } catch (err) {
      onApiError(err, 'Error downloading report');
    }
  };

  return (
    <Dialog className={classes.dialog} onClose={handleClose}>
      <DialogTitle title="Export Summary Report" onClose={handleClose} />
      <DialogBody className={classes.body}>
        <p className={classes.instructions}>
          Please select a date range for your export.
        </p>
        <DateRangeInput
          disablePast={false}
          className={classes.dateRangeInput}
          value={timeframe}
          name="timeframe"
          onApply={(update) => handleUpdate(update.timeframe)}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={isExporting}
        pending={isExporting}
        confirmLabel="Export"
        pendingLabel="Exporting..."
        onCancel={handleClose}
        onConfirm={handleExport}
      />
    </Dialog>
  );
}
